<template>
    <div
        class="delete-message-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-message-modal-title"
        aria-describedby="delete-message-modal-description"
        @click.self="closePopup"
    >
        <div class="delete-message-modal__dialog">
            <div class="delete-message-modal__body">
                <div
                    class="delete-message-modal__icon"
                    aria-hidden="true"
                >
                    <i class="fa fa-trash"></i>
                </div>

                <p
                    id="delete-message-modal-description"
                    class="delete-message-modal__message"
                >
                    Choose how you want to delete this message.
                </p>

                <p
                    v-if="canDeleteForEveryone"
                    class="delete-message-modal__description"
                >
                    You can delete this message for yourself or
                    for everyone in the room.
                </p>

                <p
                    v-else
                    class="delete-message-modal__description"
                >
                    This message can only be deleted from your
                    own chat view.
                </p>
            </div>

            <footer class="delete-message-modal__footer">
                <button
                    type="button"
                    class="
                        delete-message-modal__button
                        delete-message-modal__button--cancel
                    "
                    :disabled="submitted"
                    @click="closePopup"
                >
                    Cancel
                </button>

                <button
                    type="button"
                    class="
                        delete-message-modal__button
                        delete-message-modal__button--self
                    "
                    :disabled="submitted"
                    @click="confirmDeleteMessage(false)"
                >
                    <i
                        v-if="
                            submitted &&
                            selectedDeleteType === 'self'
                        "
                        class="fa fa-spinner fa-spin"
                        aria-hidden="true"
                    ></i>

                    <span>
                        {{
                            submitted &&
                            selectedDeleteType === 'self'
                                ? 'Deleting...'
                                : 'Delete For Me'
                        }}
                    </span>
                </button>

                <button
                    v-if="canDeleteForEveryone"
                    ref="deleteEveryoneButton"
                    type="button"
                    class="
                        delete-message-modal__button
                        delete-message-modal__button--everyone
                    "
                    :disabled="submitted"
                    @click="confirmDeleteMessage(true)"
                >
                    <i
                        v-if="
                            submitted &&
                            selectedDeleteType === 'everyone'
                        "
                        class="fa fa-spinner fa-spin"
                        aria-hidden="true"
                    ></i>

                    <span>
                        {{
                            submitted &&
                            selectedDeleteType === 'everyone'
                                ? 'Deleting...'
                                : 'Delete For Everyone'
                        }}
                    </span>
                </button>
            </footer>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import { bus } from '@/main';
import toast from '@/services/toast';

const DELETE_FOR_EVERYONE_LIMIT_MINUTES = 10;

export default {
    name: 'MessageDeletePopup',

    props: {
        deleteMessageDataOBJ: {
            type: Object,
            required: true,
            validator(value) {
                return Boolean(
                    value &&
                    value.id !== null &&
                    value.id !== undefined
                );
            }
        },

        roomSocketUrl: {
            default: null,
            validator(value) {
                return (
                    value === null ||
                    typeof value.send === 'function'
                );
            }
        }
    },

    data() {
        return {
            submitted: false,
            selectedDeleteType: '',
            previouslyFocusedElement: null
        };
    },

    computed: {
        messageId() {
            return this.deleteMessageDataOBJ &&
                this.deleteMessageDataOBJ.id !== undefined
                ? this.deleteMessageDataOBJ.id
                : null;
        },

        roomId() {
            if (!this.deleteMessageDataOBJ) {
                return null;
            }

            return (
                this.deleteMessageDataOBJ.roomId ||
                this.deleteMessageDataOBJ.room_id ||
                null
            );
        },

        loggedInUserId() {
            return localStorage.getItem(
                'LoginedUserID'
            );
        },

        isOwnMessage() {
            if (
                !this.deleteMessageDataOBJ ||
                this.deleteMessageDataOBJ.createdBy ===
                    undefined ||
                this.deleteMessageDataOBJ.createdBy ===
                    null ||
                !this.loggedInUserId
            ) {
                return false;
            }

            return (
                String(
                    this.deleteMessageDataOBJ.createdBy
                ) === String(this.loggedInUserId)
            );
        },

       canDeleteForEveryone() {
            if (
                !this.isOwnMessage ||
                !this.deleteMessageDataOBJ ||
                !this.deleteMessageDataOBJ.time
            ) {
                return false;
            }

            const messageTime = moment(this.deleteMessageDataOBJ.time);

            if (!messageTime.isValid()) {
                return false;
            }

            const elapsedMinutes = moment().diff(messageTime, 'minutes', true);

            // Allow a 1-minute drift buffer for slight client/server desync
            const SKEW_TOLERANCE_MINUTES = -1;

            return (
                elapsedMinutes >= SKEW_TOLERANCE_MINUTES &&
                elapsedMinutes < DELETE_FOR_EVERYONE_LIMIT_MINUTES
            );
        },

        hasValidMessageId() {
            return (
                this.messageId !== null &&
                this.messageId !== undefined &&
                String(this.messageId).trim() !== ''
            );
        }
    },

    mounted() {
        this.previouslyFocusedElement =
            document.activeElement;

        document.body.classList.add(
            'body_right_scroll_remove'
        );

        document.addEventListener(
            'keydown',
            this.handleDocumentKeydown
        );

        this.$nextTick(() => {
            if (
                this.canDeleteForEveryone &&
                this.$refs.deleteEveryoneButton
            ) {
                this.$refs.deleteEveryoneButton.focus();
            }
        });
    },

    beforeDestroy() {
        document.removeEventListener(
            'keydown',
            this.handleDocumentKeydown
        );

        document.body.classList.remove(
            'body_right_scroll_remove'
        );
    },

    methods: {
        async confirmDeleteMessage(
            deleteForEveryone
        ) {
            if (this.submitted) {
                return;
            }

            if (!this.hasValidMessageId) {
                toast.error(
                    'Message ID is required.'
                );
                return;
            }

            if (
                deleteForEveryone &&
                !this.canDeleteForEveryone
            ) {
                toast.error(
                    'This message can no longer be deleted for everyone.'
                );
                return;
            }

            this.submitted = true;
            this.selectedDeleteType =
                deleteForEveryone
                    ? 'everyone'
                    : 'self';

            const payload = {
                data: {
                    id: this.messageId,
                    query: deleteForEveryone
                        ? 'yes'
                        : 'no'
                }
            };

            try {
                const response =
                    await this.$store.dispatch(
                        'deleteChatMessage',
                        payload
                    );

                if (deleteForEveryone) {
                    this.handleDeleteForEveryone();
                } else {
                    this.handleDeleteForSelf();
                }

                toast.success(
                    this.getSuccessMessage(
                        response,
                        deleteForEveryone
                            ? 'Message deleted for everyone.'
                            : 'Message deleted for you.'
                    )
                );

                this.closePopup(true);
            } catch (error) {
                // toast.error(
                //     this.getErrorMessage(
                //         error,
                //         'Unable to delete the message.'
                //     )
                // );
            } finally {
                this.submitted = false;
                this.selectedDeleteType = '';
            }
        },

        handleDeleteForEveryone() {
            const deletedMessageData = {
                id: this.messageId,
                roomId: this.roomId,
                deletedForEveryone: true,
                deletedText:
                    'This message was deleted',
                time:
                    this.deleteMessageDataOBJ.time
            };

            /*
             * The parent message-list component should update
             * the message reactively when receiving this event.
             */
            bus.$emit(
                'messageDeletedBus',
                deletedMessageData
            );

            this.sendDeleteSocketEvent();
        },

        handleDeleteForSelf() {
            /*
             * The parent message-list component should remove
             * the message from its array when receiving this.
             */
            bus.$emit('messageRemovedForSelfBus', {
                id: this.messageId,
                roomId: this.roomId
            });
        },

        sendDeleteSocketEvent() {
            if (
                !this.roomSocketUrl ||
                typeof this.roomSocketUrl.send !==
                    'function'
            ) {
                console.warn(
                    'Delete-message socket is unavailable.'
                );
                return;
            }

            const openState =
                typeof WebSocket !== 'undefined'
                    ? WebSocket.OPEN
                    : 1;

            if (
                this.roomSocketUrl.readyState !==
                openState
            ) {
                console.warn(
                    'Delete-message socket is not open.'
                );
                return;
            }

            try {
                this.roomSocketUrl.send(
                    JSON.stringify({
                        type: 'delete_message',
                        chat_room_id: this.roomId,
                        message_id: this.messageId
                    })
                );
            } catch (error) {
                console.error(
                    'Unable to send delete-message socket event:',
                    error
                );
            }
        },

        closePopup(forceClose) {
            if (
                this.submitted &&
                forceClose !== true
            ) {
                return;
            }

            document.body.classList.remove(
                'body_right_scroll_remove'
            );

            bus.$emit(
                'deleteMsgPopupStateBus',
                false
            );

            this.restoreFocus();
        },

        restoreFocus() {
            const previousElement =
                this.previouslyFocusedElement;

            this.$nextTick(() => {
                if (
                    previousElement &&
                    typeof previousElement.focus ===
                        'function'
                ) {
                    previousElement.focus();
                }

                this.previouslyFocusedElement = null;
            });
        },

        handleDocumentKeydown(event) {
            if (
                event.key === 'Escape' &&
                !this.submitted
            ) {
                this.closePopup();
            }
        },

        getSuccessMessage(
            response,
            fallbackMessage
        ) {
            if (!response || !response.data) {
                return fallbackMessage;
            }

            return (
                response.data.user_msg ||
                response.data.msg ||
                response.data.message ||
                fallbackMessage
            );
        },

        getErrorMessage(
            error,
            fallbackMessage
        ) {
            const responseData =
                error &&
                error.response &&
                error.response.data
                    ? error.response.data
                    : error && error.data
                        ? error.data
                        : null;

            if (!responseData) {
                return error && error.message
                    ? error.message
                    : fallbackMessage;
            }

            const nestedData =
                responseData.data &&
                typeof responseData.data ===
                    'object'
                    ? responseData.data
                    : null;

            if (
                nestedData &&
                Array.isArray(
                    nestedData.non_field_errors
                ) &&
                nestedData.non_field_errors.length
            ) {
                return nestedData.non_field_errors[0];
            }

            if (
                Array.isArray(
                    responseData.non_field_errors
                ) &&
                responseData.non_field_errors.length
            ) {
                return responseData
                    .non_field_errors[0];
            }

            return (
                responseData.user_msg ||
                responseData.msg ||
                responseData.message ||
                fallbackMessage
            );
        }
    }
};
</script>

<style lang="scss" scoped>
$primary-text: #ffffff;
$secondary-text: #a8a8a8;
$accent-color: #87cefa;
$highlight-color: #ffe738;
$danger-color: #ff3a30;
$info-color: #05aac5;
$success-color: #21e400;
$dialog-background: #000000;
$overlay-background: rgba(0, 0, 0, 0.92);

.delete-message-modal {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    overflow-y: auto;
    color: $primary-text;
    background-color: $overlay-background;
    font-family:
        'MyriadProRegular',
        'Lato',
        sans-serif;

    &__dialog {
        width: 100%;
        max-width: 540px;
        overflow: hidden;
        border-radius: 6px;
        background-color: $dialog-background;
        box-shadow: 0 0 13px
            rgba(255, 213, 0, 0.6);
    }

    &__body {
        padding: 26px 20px 22px;
        text-align: center;
    }

    &__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 64px;
        height: 64px;
        margin-bottom: 16px;
        border-radius: 50%;
        color: $danger-color;
        background-color: rgba(
            255,
            58,
            48,
            0.12
        );
        font-size: 28px;
    }

    &__message {
        margin: 0;
        color: $primary-text;
        font-size: 18px;
        line-height: 1.5;
    }

    &__description {
        margin: 10px 0 0;
        color: $secondary-text;
        font-size: 14px;
        line-height: 1.5;
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 12px;
        padding: 0 20px 20px;
    }

    &__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 120px;
        min-height: 40px;
        padding: 8px 14px;
        border: 0;
        border-radius: 4px;
        color: $primary-text;
        font-family: inherit;
        font-size: 15px;
        cursor: pointer;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease;

        .fa {
            margin-right: 7px;
        }

        &:hover:not(:disabled){
            color: #22292f;
            background-color: $highlight-color;
        }

        &:focus {
            outline: 2px solid $accent-color;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }

        &--cancel {
            background-color: rgba(
                255,
                58,
                48,
                0.45
            );
        }

        &--self {
            background-color: rgba(
                5,
                170,
                197,
                0.5
            );
        }

        &--everyone {
            background-color: rgba(
                33,
                228,
                0,
                0.5
            );
        }
    }
}

@media only screen and (max-width: 575px) {
    .delete-message-modal {
        align-items: flex-end;
        padding: 10px;

        &__dialog {
            max-width: 100%;
        }

        &__header {
            padding: 16px;
        }

        &__body {
            padding: 22px 16px;
        }

        &__footer {
            flex-direction: column-reverse;
            padding: 0 16px 16px;
        }

        &__button {
            width: 100%;
        }
    }
}
</style>